import type {
  LiraConversationState,
  DashboardBarangayStatistics,
  LiraQueryResponse,
} from "@rbim/shared";
import { Bot, Minus, Send, Sparkles, X } from "lucide-react";
import { Fragment, useEffect, useRef, useState, type FormEvent } from "react";
import { ApiRequestError, apiRequest } from "../../api";
import { RbimAnalyticsPanel } from "../analytics/rbim-analytics";
import { reformatPreviousLiraResult } from "./lira-conversation";
import { appendLiraChatMessage, visibleLiraChatMessages, LIRA_VISIBLE_MESSAGES, liraTopicRecap, liraConversationResumeLabel } from "./lira-chat-memory";
type LiraMessage = {
  id: string;
  role: "assistant" | "user";
  text: string;
  analytics?: LiraQueryResponse["analytics"];
  resumeLabel?: string;
};

type LiraContextProps = {
  contextBarangay?: DashboardBarangayStatistics | null;
  contextBarangayId?: string | null;
  contextLabel?: string | null;
};

const welcomeMessage: LiraMessage = {
  id: "lira-welcome",
  role: "assistant",
  text: "Hi, I'm LIRA. How can I help with RBIM or population data today?",
};

export function LiraChat({
  contextBarangay,
  contextBarangayId,
  contextLabel,
  compact = false,
  onMinimize,
  onClose,
}: LiraContextProps & {
  compact?: boolean;
  onMinimize?: () => void;
  onClose?: () => void;
}) {
  const [messages, setMessages] = useState<LiraMessage[]>([welcomeMessage]);
  const [visibleCount, setVisibleCount] = useState(LIRA_VISIBLE_MESSAGES);
  const [topicRecap, setTopicRecap] = useState("");
  const conversation = useRef<LiraConversationState | null>(null);
  const lastDeterministicResult = useRef<LiraQueryResponse | null>(null);
  const lastChatActivityMs = useRef<number | null>(null);
  const [message, setMessage] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  const resolvedBarangayId = contextBarangay?.barangayId ?? contextBarangayId ?? null;
  const resolvedContextLabel = contextBarangay
    ? `Barangay context: ${contextBarangay.barangayName}`
    : contextLabel ?? "Municipality-wide context";
  const contextKey = `${resolvedBarangayId ?? "MUNICIPALITY"}|${resolvedContextLabel}`;
  const activeContextKey = useRef(contextKey);

  useEffect(() => {
    activeContextKey.current = contextKey;
    conversation.current = null;
    lastDeterministicResult.current = null;
    lastChatActivityMs.current = null;
    setMessages([welcomeMessage]);
    setVisibleCount(LIRA_VISIBLE_MESSAGES);
    setTopicRecap("");
    setMessage("");
    setPending(false);
    setError("");
  }, [contextKey]);

  async function sendMessage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextMessage = message.trim();
    if (!nextMessage || pending) return;

    const requestContextKey = contextKey;
    setMessage("");
    setError("");
    setPending(true);
    const sentAtMs = Date.now();
    const resumeLabel = liraConversationResumeLabel(lastChatActivityMs.current, sentAtMs);
    lastChatActivityMs.current = sentAtMs;
    setMessages((current) => appendLiraChatMessage(current, {
      id: crypto.randomUUID(), role: "user", text: nextMessage,
      ...(resumeLabel ? { resumeLabel } : {}),
    }));

    try {
      // The dashboard selection resets these refs. A naturally requested scope
      // survives follow-ups and remains a requested target checked by the API.
      const queryBarangayId = conversation.current ? conversation.current.barangayId : resolvedBarangayId;
      const formatted = reformatPreviousLiraResult({ message: nextMessage, conversation: conversation.current, result: lastDeterministicResult.current, barangayId: queryBarangayId });
      if (formatted) {
        conversation.current = formatted.conversation;
        lastDeterministicResult.current = formatted.result;
        setTopicRecap(liraTopicRecap(formatted.conversation?.topicRecap));
        lastChatActivityMs.current = Date.now();
        setMessages((current) => appendLiraChatMessage(current, { id: crypto.randomUUID(), role: "assistant", text: formatted.text, analytics: formatted.analytics }));
        return;
      }
      const result = await apiRequest<LiraQueryResponse>("/lira/query", {
        method: "POST",
        body: JSON.stringify({
          message: nextMessage,
          conversation: conversation.current,
          barangayId: queryBarangayId,
        }),
      });
      if (activeContextKey.current !== requestContextKey) return;
      lastChatActivityMs.current = Date.now();
      conversation.current = result.conversation ?? null;
      setTopicRecap(liraTopicRecap(result.conversation?.topicRecap));
      if (result.deterministicResult || result.analytics) lastDeterministicResult.current = result;
      setMessages((current) => appendLiraChatMessage(current, {
        id: crypto.randomUUID(),
        role: "assistant",
        text: result.answer,
        ...(result.analytics ? { analytics: result.analytics } : {}),
      }));
    } catch (requestError) {
      if (activeContextKey.current !== requestContextKey) return;
      setError(requestError instanceof ApiRequestError ? requestError.message : "LIRA could not answer right now.");
    } finally {
      if (activeContextKey.current === requestContextKey) setPending(false);
    }
  }

  return (
    <section className={compact ? "lira-chat lira-chat-compact" : "lira-chat"} aria-label="LIRA AI Assistant">
      <header className="lira-chat-header">
        <span className="lira-avatar"><Bot aria-hidden="true" /></span>
        <span className="lira-identity"><strong>LIRA</strong><small>AI Assistant</small></span>
        <span className="lira-availability"><i />Available</span>
        {onMinimize ? <button type="button" onClick={onMinimize} aria-label="Minimize LIRA"><Minus /></button> : null}
        {onClose ? <button type="button" onClick={onClose} aria-label="Close LIRA"><X /></button> : null}
      </header>

      <div className="lira-context">
        <Sparkles aria-hidden="true" />
        <span>{resolvedContextLabel}</span>
      </div>

      <div className="lira-conversation" aria-live="polite">
        {messages.length > visibleCount ? (
          <button type="button" onClick={() => setVisibleCount((n) => Math.min(messages.length, n + 60))}>
            Show earlier messages ({messages.length - visibleCount} hidden)
          </button>
        ) : null}
        {messages.length > 40 && topicRecap ? (
          <small className="lira-helper">Conversation topics: {topicRecap}. Counts are checked against current authorized RBIM data, not recalled from earlier messages.</small>
        ) : null}
        {visibleLiraChatMessages(messages, visibleCount).map((item) => (
          <Fragment key={item.id}>
            {item.resumeLabel ? (
              <div
                role="separator"
                aria-label={item.resumeLabel}
                className="lira-conversation-resumed"
                style={{ textAlign: "center", margin: "14px 0", fontSize: "0.75rem", opacity: 0.78 }}
              >
                <span>{item.resumeLabel}</span>
              </div>
            ) : null}
            <div className={`lira-message-group ${item.role}`}>
              <p className={`lira-message ${item.role}`}>{item.text}</p>
              {item.analytics ? <RbimAnalyticsPanel analytics={item.analytics} compact={compact} {...(item.analytics.scope ? { expectedBarangayId: item.analytics.scope.barangayId } : {})} /> : null}
            </div>
          </Fragment>
        ))}
        {messages.length === 1 ? (
          <small className="lira-helper">
            Ask naturally about authorized RBIM population data. I can calculate aggregate counts and percentages, compare authorized Barangays, summarize senior and migration data, and generate tables, bar/donut charts, and report-ready outputs. I do not disclose resident identities or cross your signed-in geographic scope.
          </small>
        ) : null}
        {pending ? <p className="lira-message assistant">Checking authorized RBIM data…</p> : null}
        {error ? <p className="lira-error" role="alert">{error}</p> : null}
      </div>

      <form className="lira-input" onSubmit={sendMessage}>
        <label className="sr-only" htmlFor={compact ? "lira-widget-input" : "lira-page-input"}>Ask LIRA</label>
        <input
          id={compact ? "lira-widget-input" : "lira-page-input"}
          value={message}
          maxLength={1_000}
          placeholder="Ask about RBIM data, comparisons, tables, charts, or reports…"
          onChange={(event) => setMessage(event.currentTarget.value)}
        />
        <button type="submit" disabled={pending || !message.trim()} aria-label="Send question to LIRA"><Send /></button>
      </form>
    </section>
  );
}

export function LiraWidget({
  contextBarangay,
  contextBarangayId,
  contextLabel,
}: LiraContextProps) {
  const [open, setOpen] = useState(false);

  return (
    <aside className="lira-widget" aria-label="Dashboard LIRA assistant">
      {/* Keep the chat mounted while minimized; never persist RBIM chat data
          to browser storage or leak it across authenticated page unmounts. */}
      <div style={{ display: open ? undefined : "none" }}>
        <LiraChat
          compact
          {...(contextBarangay === undefined ? {} : { contextBarangay })}
          {...(contextBarangayId === undefined ? {} : { contextBarangayId })}
          {...(contextLabel === undefined ? {} : { contextLabel })}
          onMinimize={() => setOpen(false)}
          onClose={() => setOpen(false)}
        />
      </div>
      {!open ? (
        <button type="button" className="lira-launcher" onClick={() => setOpen(true)} aria-label="Open LIRA AI Assistant">
          <span><Bot aria-hidden="true" /></span>
          <span><strong>LIRA</strong><small>AI Assistant</small></span>
          <i aria-hidden="true" />
        </button>
      ) : null}
    </aside>
  );
}
